import { usePage } from "@inertiajs/vue3";
import { computed } from "vue";
import { isAndroid } from "@/Helpers/capacitor.js";
import { PlayAlternativeBillingNative } from "@/Helpers/playAlternativeBillingNative.js";
import axios from "axios";
import {
    createPendingRecord,
    storeToken,
    submitPending,
    sweepPending,
    findPendingWithoutToken,
    findPendingByUniqueIdentifier,
    clearPendingRecord,
    isRecordDueForRetry,
    recordAttempt,
} from "@/Helpers/playAlternativeBillingQueue.js";

const BILLING_RESPONSE_CODE_OK = 0;
const BILLING_RESPONSE_CODE_USER_CANCELED = 1;

export const useAltBillingEligible = () => computed(() => isAndroid());

const logAltBillingFailure = (stage, error) => {
    axios
        .post(route("paywall.google_play.log_alternative_billing_failure"), {
            stage,
            message: error?.message ?? String(error),
        })
        .catch(() => {});
};

export const initializeAltBilling = async () => {
    try {
        await PlayAlternativeBillingNative.initialize();
    } catch (error) {
        logAltBillingFailure("initialize", error);

        return false;
    }

    try {
        const { available } = await PlayAlternativeBillingNative.isAvailable();

        return !!available;
    } catch (error) {
        logAltBillingFailure("is_available", error);

        return false;
    }
};

export const showAltBillingInformationDialog = async () => {
    try {
        const { responseCode } =
            await PlayAlternativeBillingNative.showInformationDialog();

        return {
            accepted: responseCode === BILLING_RESPONSE_CODE_OK,
            canceled: responseCode === BILLING_RESPONSE_CODE_USER_CANCELED,
        };
    } catch {
        return { accepted: false, canceled: false };
    }
};

export const beginAltBillingPurchase = async ({
    purchaseAttemptId,
    productId,
    orderType,
    uniqueIdentifier,
}) => {
    if (!uniqueIdentifier) {
        return null;
    }

    const existing = await findPendingByUniqueIdentifier(uniqueIdentifier);
    if (existing) {
        if (
            existing.productId === productId &&
            existing.orderType === orderType
        ) {
            return existing.purchaseAttemptId;
        }

        await clearPendingRecord(existing.purchaseAttemptId);
    }

    await createPendingRecord({
        purchaseAttemptId,
        productId,
        orderType,
        uniqueIdentifier,
    });

    return purchaseAttemptId;
};

export const completeAltBillingPurchase = async (purchaseAttemptId) => {
    try {
        const { externalTransactionToken } =
            await PlayAlternativeBillingNative.createReportingDetails();

        if (!externalTransactionToken) {
            return;
        }

        await storeToken(purchaseAttemptId, externalTransactionToken);
        await submitPending(purchaseAttemptId);
    } catch {}
};

export const cancelAltBillingPurchase = (purchaseAttemptId) =>
    clearPendingRecord(purchaseAttemptId);

export const resumePendingAltBillingPurchases = () => sweepPending();

export const resumeIncompleteAltBillingPurchase = async () => {
    const record = await findPendingWithoutToken();
    if (!record) {
        return;
    }

    if (usePage().props.flash?.banner === "PaymentFailedModal") {
        await clearPendingRecord(record.purchaseAttemptId);
        return;
    }

    if (!isRecordDueForRetry(record)) {
        return;
    }

    await recordAttempt(record.purchaseAttemptId);
    await initializeAltBilling();
    await completeAltBillingPurchase(record.purchaseAttemptId);
};
